import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart3, CheckCircle, XCircle, MessageSquare, Clock, TrendingUp, FileCheck, Info } from 'lucide-react';
import { toast } from '../../lib/safeToast';

function formatDt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

function KpiCard({ icon: Icon, title, value, accent, onClick }) {
  return (
    <Card
      className={`border-[#DCE4E0] shadow-sm transition-all ${onClick ? 'cursor-pointer hover:border-[#0B6445]/50 hover:shadow-md group' : ''}`}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accent || ''}`} />
          {title}
          {onClick && <Info className="w-3 h-3 ml-auto text-slate-300 group-hover:text-[#0B6445] transition-colors" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-bold text-[#073B2E]">{value ?? '—'}</p>
      </CardContent>
    </Card>
  );
}

function DetailDialog({ open, onClose, title, description, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#073B2E]">{title}</DialogTitle>
          <DialogDescription className="text-slate-500">{description}</DialogDescription>
        </DialogHeader>
        <div className="mt-2">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

function ActionTable({ items, showDoc }) {
  if (!items || items.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No records.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Merchant</th>
            {showDoc && <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Document</th>}
            <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Comments</th>
            <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Date</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.id || i} className="border-b border-slate-100 last:border-0">
              <td className="py-2 px-2">
                <div className="font-medium text-slate-700">{r.merchant_name || r.merchant_email || '—'}</div>
                {r.company_name && <div className="text-xs text-slate-400">{r.company_name}</div>}
              </td>
              {showDoc && (
                <td className="py-2 px-2">
                  <Badge variant="outline" className="text-xs">{(r.doc_type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—'}</Badge>
                </td>
              )}
              <td className="py-2 px-2 text-slate-600 max-w-[200px] truncate">{r.comments || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{formatDt(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BackOfficeCompliancePerformance() {
  const { api, user } = useAuth();
  const { isLive } = useEnvironment();
  const [stats, setStats] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [officers, setOfficers] = useState([]);
  const [selectedCo, setSelectedCo] = useState('__all__');
  const [activeDialog, setActiveDialog] = useState(null);

  const isSU = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchOfficers = useCallback(async () => {
    if (!isSU) return;
    try {
      const res = await api.get('/backoffice/compliance-officers');
      setOfficers(res.data?.data || res.data || []);
    } catch {
      setOfficers([]);
    }
  }, [api, isSU]);

  const fetchStats = useCallback(async (coId) => {
    setLoading(true);
    try {
      const params = coId ? `?co_id=${coId}` : '';
      const res = await api.get(`/backoffice/kyc/co-performance${params}`);
      setStats(res.data?.data || null);
      setDetails(res.data?.details || null);
    } catch {
      setStats(null);
      setDetails(null);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchOfficers(); }, [fetchOfficers]);
  useEffect(() => { fetchStats(selectedCo === '__all__' ? undefined : selectedCo); }, [fetchStats, selectedCo, isLive]);

  const s = stats || {};
  const d = details || {};

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-[#0B6445]" />
              CO Performance
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">Compliance officer review statistics and KPIs. Click any card to view details.</p>
          </div>
          {isSU && officers.length > 0 && (
            <div className="w-64">
              <Select value={selectedCo} onValueChange={setSelectedCo}>
                <SelectTrigger className="text-[#0F172A]">
                  <SelectValue placeholder="All compliance officers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Officers</SelectItem>
                  {officers.map((co) => (
                    <SelectItem key={co.id} value={String(co.id)}>{co.name || co.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : !stats ? (
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardContent className="pt-6">
              <p className="text-center py-12 text-[#0E1B16]/60">No performance data available.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard icon={FileCheck} title="Merchants Reviewed" value={s.total_reviewed ?? 0}
                onClick={() => setActiveDialog('merchants')} />
              <KpiCard icon={FileCheck} title="Total Actions" value={s.total_actions ?? 0}
                onClick={() => setActiveDialog('actions')} />
              <KpiCard icon={TrendingUp} title="Approval Rate" value={s.approval_rate != null ? `${s.approval_rate}%` : '—'} accent="text-[#0B6445]"
                onClick={() => setActiveDialog('approval_rate')} />
              <KpiCard icon={Clock} title="Avg Review Time" value={s.avg_review_time_hours != null ? `${s.avg_review_time_hours}h` : '—'} accent="text-blue-600"
                onClick={() => setActiveDialog('review_time')} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <KpiCard icon={CheckCircle} title="Docs Accepted" value={s.docs_accepted ?? 0} accent="text-emerald-600"
                onClick={() => setActiveDialog('docs_accepted')} />
              <KpiCard icon={MessageSquare} title="Updates Requested" value={s.docs_update_requested ?? 0} accent="text-amber-600"
                onClick={() => setActiveDialog('docs_update')} />
              <KpiCard icon={MessageSquare} title="Info Requested" value={s.info_requested ?? 0} accent="text-amber-600"
                onClick={() => setActiveDialog('info_req')} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <KpiCard icon={CheckCircle} title="Recommended Approve" value={s.approved ?? 0} accent="text-emerald-600"
                onClick={() => setActiveDialog('rec_approve')} />
              <KpiCard icon={XCircle} title="Recommended Reject" value={s.rejected ?? 0} accent="text-red-600"
                onClick={() => setActiveDialog('rec_reject')} />
            </div>
          </div>
        )}
      </div>

      {/* ── Detail Dialogs ── */}

      <DetailDialog open={activeDialog === 'merchants'} onClose={() => setActiveDialog(null)}
        title={`Merchants Reviewed — ${s.total_reviewed ?? 0}`}
        description="Unique merchants this officer has performed any review action on.">
        {(d.merchants_reviewed || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Merchant</th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Actions</th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">First Action</th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Last Action</th>
              </tr></thead>
              <tbody>
                {(d.merchants_reviewed || []).map((m, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-700">{m.merchant_name || '—'}</div>
                      {m.company_name && <div className="text-xs text-slate-400">{m.company_name}</div>}
                    </td>
                    <td className="py-2 px-2 font-semibold text-[#073B2E]">{m.action_count}</td>
                    <td className="py-2 px-2 text-xs text-slate-400">{formatDt(m.first_action)}</td>
                    <td className="py-2 px-2 text-xs text-slate-400">{formatDt(m.last_action)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-400 py-4 text-center">No merchants reviewed yet.</p>}
      </DetailDialog>

      <DetailDialog open={activeDialog === 'actions'} onClose={() => setActiveDialog(null)}
        title={`Total Actions — ${s.total_actions ?? 0}`}
        description="All review actions performed: document reviews, update requests, and formal recommendations.">
        <ActionTable items={d.all_actions} showDoc />
      </DetailDialog>

      <DetailDialog open={activeDialog === 'approval_rate'} onClose={() => setActiveDialog(null)}
        title={`Approval Rate — ${s.approval_rate ?? 0}%`}
        description="Percentage of unique merchants where the latest CO recommendation was 'Approve'.">
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center">
              <p className="text-2xl font-bold text-emerald-700">{s.approved ?? 0}</p>
              <p className="text-xs text-emerald-600 mt-0.5">Approved</p>
            </div>
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{s.rejected ?? 0}</p>
              <p className="text-xs text-red-600 mt-0.5">Rejected</p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{s.info_requested ?? 0}</p>
              <p className="text-xs text-amber-600 mt-0.5">Info Requested</p>
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
            <p><strong>Calculation:</strong> Approved / Total Merchants Reviewed × 100</p>
            <p className="mt-1">{s.approved ?? 0} / {s.total_reviewed ?? 0} = <strong>{s.approval_rate ?? 0}%</strong></p>
            <p className="mt-1 text-slate-400">Based on the latest recommendation per merchant. If the same merchant was reviewed multiple times, only the most recent recommendation counts.</p>
          </div>
        </div>
      </DetailDialog>

      <DetailDialog open={activeDialog === 'review_time'} onClose={() => setActiveDialog(null)}
        title={`Avg Review Time — ${s.avg_review_time_hours != null ? `${s.avg_review_time_hours}h` : '—'}`}
        description="Average time from CO assignment to first review action, per merchant.">
        {(d.review_times || []).length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Merchant</th>
                  <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Assigned</th>
                  <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">First Action</th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500">Hours</th>
                </tr></thead>
                <tbody>
                  {(d.review_times || []).map((rt, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 px-2">
                        <div className="font-medium text-slate-700">{rt.merchant_name || '—'}</div>
                        {rt.company_name && <div className="text-xs text-slate-400">{rt.company_name}</div>}
                      </td>
                      <td className="py-2 px-2 text-xs text-slate-400">{formatDt(rt.assigned_at)}</td>
                      <td className="py-2 px-2 text-xs text-slate-400">{formatDt(rt.first_action_at)}</td>
                      <td className="py-2 px-2 text-right font-semibold text-[#073B2E]">{rt.hours != null ? `${rt.hours}h` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 mt-3">
              <p><strong>Calculation:</strong> Average of (First Action Time − Assignment Time) across all merchants.</p>
              <p className="mt-1">Sum = {(d.review_times || []).filter(r => r.hours != null).map(r => `${r.hours}h`).join(' + ')} = {s.avg_review_time_hours}h avg</p>
            </div>
          </>
        ) : <p className="text-sm text-slate-400 py-4 text-center">No review time data available.</p>}
      </DetailDialog>

      <DetailDialog open={activeDialog === 'docs_accepted'} onClose={() => setActiveDialog(null)}
        title={`Documents Accepted — ${s.docs_accepted ?? 0}`}
        description="Individual documents reviewed and accepted by this officer.">
        <ActionTable items={d.docs_accepted} showDoc />
      </DetailDialog>

      <DetailDialog open={activeDialog === 'docs_update'} onClose={() => setActiveDialog(null)}
        title={`Updates Requested — ${s.docs_update_requested ?? 0}`}
        description="Documents where the officer requested the merchant to re-upload.">
        <ActionTable items={d.docs_update_requested} showDoc />
      </DetailDialog>

      <DetailDialog open={activeDialog === 'info_req'} onClose={() => setActiveDialog(null)}
        title={`Info Requested — ${s.info_requested ?? 0}`}
        description="Formal review submissions where the officer requested additional information.">
        <ActionTable items={d.recommendations_info} />
      </DetailDialog>

      <DetailDialog open={activeDialog === 'rec_approve'} onClose={() => setActiveDialog(null)}
        title={`Recommended Approve — ${s.approved ?? 0}`}
        description="Merchants where the latest CO recommendation was to approve.">
        <ActionTable items={d.recommendations_approve} />
      </DetailDialog>

      <DetailDialog open={activeDialog === 'rec_reject'} onClose={() => setActiveDialog(null)}
        title={`Recommended Reject — ${s.rejected ?? 0}`}
        description="Merchants where the latest CO recommendation was to reject.">
        <ActionTable items={d.recommendations_reject} />
      </DetailDialog>

    </BackOfficeLayout>
  );
}
